import {
  useJsApiLoader,
  StandaloneSearchBox,
  GoogleMap,
  Marker,
  Circle,
} from '@react-google-maps/api';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Crosshair, Loader2, X } from 'lucide-react';

export interface LocationData {
  address: string;
  coordinates: {
    lat: number;
    lng: number;
  };
}

export interface ZoneRestriction {
  center: { lat: number; lng: number };
  radius: number; // in meters
}

interface GoogleMapPickerProps {
  label?: string;
  value?: LocationData;
  onChange?: (location: LocationData) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Shows the map but refuses new pins — used when the address is fixed elsewhere. */
  readOnly?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  className?: string;
  height?: string;
  apiKey?: string;
  defaultCenter?: { lat: number; lng: number };
  defaultZoom?: number;
  /**
   * Drawn on the map purely as guidance. Pins outside it are still accepted —
   * whether that matters is the caller's decision, not this component's.
   */
  zoneRestriction?: ZoneRestriction;
}

const JAMAICA_CENTER = { lat: 18.1096, lng: -77.2975 };
const DEFAULT_ZOOM = 9;
const SELECTED_ZOOM = 15;
const LIBRARIES: 'places'[] = ['places'];

const formatCoordinates = (lat: number, lng: number) =>
  `${lat.toFixed(6)}, ${lng.toFixed(6)}`;

const GoogleMapPicker: React.FC<GoogleMapPickerProps> = ({
  label,
  value,
  onChange,
  placeholder = 'Search for an address...',
  disabled = false,
  readOnly = false,
  required = false,
  fullWidth = false,
  className = '',
  height = '320px',
  apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '',
  defaultCenter = JAMAICA_CENTER,
  defaultZoom = DEFAULT_ZOOM,
  zoneRestriction,
}) => {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [searchBox, setSearchBox] =
    useState<google.maps.places.SearchBox | null>(null);
  const [marker, setMarker] = useState<{ lat: number; lng: number } | null>(
    value?.coordinates ?? null
  );
  const [searchValue, setSearchValue] = useState(value?.address || '');
  const [locationError, setLocationError] = useState<string | null>(null);
  const [isGettingLocation, setIsGettingLocation] = useState(false);

  const locked = disabled || readOnly;

  // onChange lives in a ref so the map callbacks below stay stable across the
  // parent's re-renders — an unstable onClick makes the map drop clicks.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Follow the value the parent holds, so an address cleared or set upstream
  // is reflected on the map.
  useEffect(() => {
    setMarker(value?.coordinates ?? null);
    if (value?.address !== undefined) {
      setSearchValue(value.address);
    }
  }, [value?.coordinates?.lat, value?.coordinates?.lng, value?.address]);

  const onMapLoad = useCallback((mapInstance: google.maps.Map) => {
    setMap(mapInstance);
  }, []);

  /**
   * Turns a coordinate into a human address. A failed lookup is not fatal —
   * the pin is what the driver navigates by, so we fall back to coordinates
   * rather than discarding the customer's selection.
   */
  const commitLocation = useCallback(async (lat: number, lng: number) => {
    const coordinates = { lat, lng };
    setMarker(coordinates);

    let address = formatCoordinates(lat, lng);
    try {
      if (typeof google !== 'undefined' && google.maps?.Geocoder) {
        const geocoder = new google.maps.Geocoder();
        const response = await geocoder.geocode({ location: coordinates });
        if (response?.results?.length) {
          address = response.results[0].formatted_address || address;
        }
      }
    } catch (error) {
      console.error('Error reverse geocoding:', error);
    }

    setSearchValue(address);
    onChangeRef.current?.({ address, coordinates });
  }, []);

  const onMapClick = useCallback(
    (event: google.maps.MapMouseEvent) => {
      if (!event.latLng) return;
      commitLocation(event.latLng.lat(), event.latLng.lng());
    },
    [commitLocation]
  );

  const onSearchBoxLoad = useCallback((ref: google.maps.places.SearchBox) => {
    setSearchBox(ref);
  }, []);

  const onPlacesChanged = useCallback(() => {
    const place = searchBox?.getPlaces()?.[0];
    const location = place?.geometry?.location;
    if (!location) return;

    const lat = location.lat();
    const lng = location.lng();
    setMarker({ lat, lng });
    setSearchValue(place?.formatted_address || formatCoordinates(lat, lng));
    onChangeRef.current?.({
      address: place?.formatted_address || formatCoordinates(lat, lng),
      coordinates: { lat, lng },
    });
    map?.panTo({ lat, lng });
    map?.setZoom(SELECTED_ZOOM);
  }, [searchBox, map]);

  const getCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by this browser');
      return;
    }

    setLocationError(null);
    setIsGettingLocation(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        commitLocation(latitude, longitude);
        map?.panTo({ lat: latitude, lng: longitude });
        map?.setZoom(SELECTED_ZOOM);
        setIsGettingLocation(false);
      },
      (error) => {
        let errorMessage = 'Unable to get your current location.';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage =
              'Location access was denied. Enable location services in your browser settings, or drop a pin on the map instead.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage =
              'Location information is unavailable. Drop a pin on the map instead.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out. Please try again.';
            break;
        }
        setLocationError(errorMessage);
        setIsGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  }, [commitLocation, map]);

  const { isLoaded: apiLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: apiKey,
    libraries: LIBRARIES,
  });

  const renderLabel = () =>
    label ? (
      <label
        className={`block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 ${
          disabled ? 'opacity-50' : ''
        }`}
      >
        {label} {required && <span className="text-red-500">*</span>}
      </label>
    ) : null;

  if (!apiKey) {
    return (
      <div className={className} data-testid="google-map-picker">
        {renderLabel()}
        <div className="border border-gray-300 dark:border-gray-600 rounded-md p-4 text-center text-sm text-gray-500 dark:text-gray-400">
          Google Maps API key is required for map functionality
        </div>
      </div>
    );
  }

  if (!apiLoaded) {
    return (
      <div className={className} data-testid="google-map-picker">
        {renderLabel()}
        <div className="border border-gray-300 dark:border-gray-600 rounded-md p-4 text-center text-sm text-gray-500 dark:text-gray-400">
          Loading map...
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${fullWidth ? 'w-full' : ''} ${className}`}
      data-testid="google-map-picker"
    >
      {renderLabel()}

      <div className="space-y-2">
        {/* Search + current location */}
        <div className="flex space-x-2">
          <div className="flex-1">
            <StandaloneSearchBox
              onLoad={onSearchBoxLoad}
              onPlacesChanged={onPlacesChanged}
            >
              <input
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                disabled={locked}
                placeholder={placeholder}
                aria-label="Search for a delivery address"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-gray-50 dark:disabled:bg-gray-800 disabled:text-gray-500"
              />
            </StandaloneSearchBox>
          </div>
          <button
            type="button"
            onClick={getCurrentLocation}
            disabled={locked || isGettingLocation}
            title="Use my current location"
            aria-label="Use my current location"
            className="px-3 py-2 bg-primary text-white rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isGettingLocation ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Crosshair className="h-5 w-5" />
            )}
          </button>
        </div>

        {!locked && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Tap the map to drop a pin, or drag the pin to fine-tune the exact
            spot.
          </p>
        )}

        {/* Map */}
        <div
          style={{ height }}
          className="border border-gray-300 dark:border-gray-600 rounded-md overflow-hidden"
        >
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={marker || zoneRestriction?.center || defaultCenter}
            zoom={marker || zoneRestriction ? SELECTED_ZOOM : defaultZoom}
            onLoad={onMapLoad}
            onClick={locked ? undefined : onMapClick}
            options={{
              zoomControl: true,
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: false,
              draggable: true,
              scrollwheel: true,
              clickableIcons: false,
            }}
          >
            {zoneRestriction && (
              <Circle
                center={zoneRestriction.center}
                radius={zoneRestriction.radius}
                options={{
                  fillColor: '#2563EB',
                  fillOpacity: 0.08,
                  strokeColor: '#2563EB',
                  strokeOpacity: 0.6,
                  strokeWeight: 2,
                  clickable: false,
                }}
              />
            )}
            {marker && (
              <Marker
                position={marker}
                draggable={!locked}
                onDragEnd={(e) => {
                  if (e.latLng) {
                    commitLocation(e.latLng.lat(), e.latLng.lng());
                  }
                }}
              />
            )}
          </GoogleMap>
        </div>

        {locationError && (
          <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-2">
            <div className="flex items-start justify-between gap-2">
              <span>{locationError}</span>
              <button
                type="button"
                onClick={() => setLocationError(null)}
                title="Dismiss"
                aria-label="Dismiss location error"
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {marker && (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Coordinates: {formatCoordinates(marker.lat, marker.lng)}
          </div>
        )}
      </div>
    </div>
  );
};

export default GoogleMapPicker;
